/**
 * Created by katarinababic on 23.5.25.
 */
import * as React from 'react';
import { EventEntry, getEventStartDateTime, getEventEarliestInterval } from '../../Model/Coach/Schedule/EventEntry';
import { useRootStore } from '../../Store/useRootStore';
import { useTrans } from '../../Store/System/LocalizationStore';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { Theme } from '../../Theme/Theme';
import DatePicker from 'react-datepicker';
import { DurationBefore, NotificationInterval } from '../../Model/Coach/Schedule/NotificationInterval';
import { RecurringPattern } from '../../Model/Coach/Schedule/RecurringPattern';
import { DayOfWeek, IsoDayOfWeekList } from '../../Model/DayOfWeek';
import { Global, css } from '@emotion/react';
import { createRecurringPattern } from '../../Model/Coach/Schedule/RecurringPattern';

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 12px;
  color: ${Theme.Colors.black};
`;

const SwitchItem = styled.div`
  display: flex;
  justify-content: space-between;
  width: 50%;
  align-items: center;
  margin-top: 32px;
`;

const SwitchLabel = styled.span`
  margin-left: 8px;
  color: ${Theme.Colors.black};
`;

const Icon = styled.i`
  color: ${Theme.Colors.warmGrey};
  font-size: 1.2em;
`;

const dropdownArrow = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8">
     <path d="M1 1l5 5 5-5" stroke="${Theme.Colors.lightGrey}" stroke-width="2" fill="none"/>
   </svg>`,
);

const ReminderTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-top: 24px;
  margin-bottom: 8px;
  color: ${Theme.Colors.black};
`;

const ReminderSelect = styled.select`
  width: 50%;
  padding: 12px;
  padding-right: 32px;
  /* remove native arrow */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  /* custom arrow via SVG background */
  background: white url('data:image/svg+xml;charset=UTF-8,${dropdownArrow}') no-repeat right 12px center;
  background-size: 12px 8px;
  border: 1px solid ${Theme.Colors.lightGrey};
  border-radius: 8px;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  color: ${Theme.Colors.black};
  &:focus {
    outline: none;
    border: 1px solid ${Theme.Colors.lightGrey};
    box-shadow: none;
  }
`;

const WeekdayContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const DayButton = styled.button<{ selected: boolean }>`
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: ${(props) => (props.selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  color: ${(props) => (props.selected ? Theme.Colors.white : Theme.Colors.black)};
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  margin-left: 8px;
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${Theme.Colors.lightGrey};
    border-radius: 10px;
    transition: 0.2s;
  }
  span:before {
    position: absolute;
    content: '';
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    border-radius: 50%;
    transition: 0.2s;
  }
  input:checked + span {
    background-color: ${Theme.Colors.primary};
  }
  input:checked + span:before {
    transform: translateX(16px);
  }
`;

export type ScheduleMenuProps = {
  event: EventEntry;
  onChange: (event: EventEntry) => void;
  disableRecurring?: boolean;
};

export const ScheduleMenu: React.FC<ScheduleMenuProps> = ({ event, onChange, disableRecurring }) => {
  const { language } = useRootStore();
  const { t } = useTrans();

  const minDate = new Date();

  const date = getEventStartDateTime(event);
  const isRecurring = !!event.recurringPattern;
  const earliestReminder = getEventEarliestInterval(event);

  const title = t('scheduler.title', { defaultValue: 'When do you want to schedule?' });

  const [selectedDays, setSelectedDays] = React.useState<DayOfWeek[]>(
    () => event.recurringPattern?.daysOfWeek.slice() ?? [],
  );

  const handleChangeDate = React.useCallback(
    (value?: Date) => {
      if (value) {
        const day = dayjs(value);
        onChange({
          ...event,
          startDate: day.format('YYYY-MM-DD'),
          endDate: day.add(1, 'year').format('YYYY-MM-DD'),
          startTime: day.format('HH:mm:ss'),
          endTime: day.format('HH:mm:ss'),
          timezone: language.timeZone,
        });
      }
    },
    [language, event, onChange],
  );

  const handleChangeReminder: React.ChangeEventHandler<HTMLSelectElement> = React.useCallback(
    (e) => {
      const value = e.target.value as DurationBefore | 'NONE';
      if (value !== 'NONE') {
        onChange({
          ...event,
          notificationIntervals: [
            {
              title: '',
              message: '',
              durationBefore: value,
              customData: {},
            },
          ],
        });
      } else {
        onChange({
          ...event,
          notificationIntervals: [],
        });
      }
    },
    [event, onChange],
  );

  const handleToggleDay = React.useCallback(
    (day: DayOfWeek) => {
      const newDays = selectedDays.includes(day) ? selectedDays.filter((d) => d !== day) : [...selectedDays, day];
      setSelectedDays(newDays);
      if (event.recurringPattern) {
        onChange({
          ...event,
          recurringPattern: { ...event.recurringPattern, daysOfWeek: newDays },
        });
      }
    },
    [selectedDays, event, onChange],
  );

  const handleToggleRecurring: React.ChangeEventHandler<HTMLInputElement> = React.useCallback(
    (e) => {
      const checked = e.target.checked;
      if (checked) {
        if (!event.recurringPattern) {
          const newPattern = createRecurringPattern({ type: 'weekly', daysOfWeek: [] });
          onChange({ ...event, recurringPattern: newPattern });
          setSelectedDays([]);
        }
      } else {
        onChange({ ...event, recurringPattern: undefined });
        setSelectedDays([]);
      }
    },
    [event, onChange],
  );

  return (
    <React.Fragment>
      <Title>{title}</Title>
      <Global
        styles={css`
          .react-datepicker {
            font-family: 'Montserrat', sans-serif;
          }
          .react-datepicker__header {
            background-color: ${Theme.Colors.primary};
          }
          .react-datepicker__current-month,
          .react-datepicker__day-name {
            color: white;
            font-family: 'Montserrat', sans-serif;
          }
          .react-datepicker__day--selected,
          .react-datepicker__day--keyboard-selected {
            background-color: ${Theme.Colors.primary};
            color: white;
          }
          /* Time selection styling: override default blue with primary */
          .react-datepicker__time-container .react-datepicker__time-list .react-datepicker__time-list-item--selected,
          .react-datepicker__time-container
            .react-datepicker__time-list
            .react-datepicker__time-list-item--keyboard-selected {
            background-color: ${Theme.Colors.primary} !important;
            color: white !important;
          }

          /* Calendar input styling */
          /* Ensure datepicker input wrapper spans full width */
          .react-datepicker-wrapper {
            display: block !important;
            width: 50% !important;
          }
          .react-datepicker__input-container {
            display: block;
            width: 100%;
          }
          .react-datepicker__input-container input {
            padding: 12px;
            border-radius: 8px;
            border: 1px solid ${Theme.Colors.lightGrey};
            font-family: 'Montserrat', sans-serif;
            font-size: 16px;
            width: 100%;
          }
          .react-datepicker__input-container input:focus {
            outline: none;
            border: 1px solid ${Theme.Colors.lightGrey};
            box-shadow: none;
          }
        `}
      />
      <DatePicker
        selected={date && date.isValid() ? date.toDate() : null}
        onChange={(d) => handleChangeDate(d as Date)}
        showTimeSelect
        timeFormat="h:mm aa"
        dateFormat="EEEE, MMMM d, yyyy h:mm aa"
        minDate={minDate}
      />
      <ReminderTitle>{t('scheduler.remindMe', { defaultValue: 'Remind me' })}</ReminderTitle>
      <ReminderSelect value={earliestReminder?.durationBefore} onChange={handleChangeReminder}>
        <option value={DurationBefore.ONE_HOUR_BEFORE}>
          {t('scheduler.remindHour', { defaultValue: 'an hour prior' })}
        </option>
        <option value={DurationBefore.HALF_HOUR_BEFORE}>
          {t('scheduler.remind30min', { defaultValue: '30 minutes prior' })}
        </option>
        <option value={DurationBefore.MIN_15_BEFORE}>
          {t('scheduler.remind15min', { defaultValue: '15 minutes prior' })}
        </option>
        <option value={DurationBefore.AT_EVENT}>
          {t('scheduler.remindWhenStarts', { defaultValue: 'when it starts' })}
        </option>
        <option value="NONE">{t('scheduler.remindNone', { defaultValue: 'do not remind me' })}</option>
      </ReminderSelect>
      {!disableRecurring && (
        <React.Fragment>
          <SwitchItem>
            <div>
              <Icon className="bi bi-arrow-repeat" />
              <SwitchLabel>{t('scheduler.repeat', { defaultValue: 'Repeat' })}</SwitchLabel>
            </div>
            <ToggleSwitch>
              <input type="checkbox" role="switch" checked={isRecurring} onChange={handleToggleRecurring} />
              <span />
            </ToggleSwitch>
          </SwitchItem>
          {isRecurring && (
            <WeekdayContainer>
              {IsoDayOfWeekList.map((day) => (
                <DayButton
                  key={day}
                  type="button"
                  selected={selectedDays.includes(day)}
                  onClick={() => handleToggleDay(day)}
                >
                  {t(`globals.dayOfWeek.${day}`).slice(0, 2)}
                </DayButton>
              ))}
            </WeekdayContainer>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
